/**
 * Iter55 - pure logic for parsing send-response bodies from
 * POST /api/estimates/{id}/send and POST /api/invoices/{id}/send.
 *
 * Extracted from the render layer so it can be unit-tested without a DOM,
 * axios, or the toast library. Both EstimateEditor.jsx and
 * InvoiceProfile.jsx route their responses through this helper so the
 * user experience is identical.
 *
 * Backend contract (Iter55):
 *   200 + { ok: true,  correlation_id, public_url, estimate|invoice }
 *   200 + { ok: false, correlation_id, public_url, estimate|invoice,
 *           detail, error_kind: "provider_failure", provider_status }
 *   400 + { detail: { detail, correlation_id, public_url } }
 *   409 + { detail: { detail, correlation_id, prior_correlation_id,
 *                     public_url, state: "in_progress" | "already_sent" } }
 *
 * Returns a discriminated union that the caller renders as a toast.
 */
export function parseSendResponse({ status, data, kind }) {
  const docField = kind === "invoice" ? "invoice" : "estimate";
  const isObj = data && typeof data === "object" && !Array.isArray(data);

  // 200 with structured body
  if (status === 200 && isObj && typeof data.ok === "boolean") {
    if (data.ok === true) {
      return {
        kind: "success",
        message: "Emailed to customer",
        correlationId: data.correlation_id || null,
        priorCorrelationId: null,
        publicUrl: data.public_url || null,
        document: data[docField] || null,
        canRetry: false,
        showSentBadge: true,
      };
    }
    // ok:false - provider failure
    return {
      kind: "provider_failure",
      message: data.detail || "Delivery service is unreachable right now.",
      correlationId: data.correlation_id || null,
      priorCorrelationId: null,
      publicUrl: data.public_url || null,
      document: data[docField] || null,
      canRetry: true,
      showSentBadge: false,
    };
  }

  // 409 conflict - dedup window active
  if (status === 409 && isObj) {
    const d = (isObj && typeof data.detail === "object" && data.detail) || data;
    const state = d.state || "in_progress";
    const alreadySent = state === "already_sent";
    return {
      kind: alreadySent ? "already_sent" : "in_progress",
      message: d.detail || (alreadySent
        ? "This was just sent - wait a moment before retrying."
        : "A send is already in progress - please wait."),
      correlationId: d.correlation_id || null,
      priorCorrelationId: d.prior_correlation_id || null,
      publicUrl: d.public_url || null,
      document: null,
      canRetry: false,
      showSentBadge: false,
    };
  }

  // 400 - validation
  if (status === 400 && isObj) {
    const d = (typeof data.detail === "object" && data.detail) || null;
    return {
      kind: "validation",
      message: (d && d.detail) || (typeof data.detail === "string" ? data.detail : "Send failed."),
      correlationId: (d && d.correlation_id) || null,
      priorCorrelationId: null,
      publicUrl: (d && d.public_url) || null,
      document: null,
      canRetry: false,
      showSentBadge: false,
    };
  }

  // Anything else (5xx HTML from Cloudflare, network error)
  return {
    kind: "unknown",
    message: (isObj && typeof data.detail === "string" && data.detail) ||
             "Send failed - please try again in a moment.",
    correlationId: (isObj && data.correlation_id) || null,
    priorCorrelationId: null,
    publicUrl: (isObj && data.public_url) || null,
    document: null,
    canRetry: true,
    showSentBadge: false,
  };
}
